import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyInventoryItemKey,
  ShopifyInventoryLevelKey,
  ShopifyInventoryQuantityKey,
  ShopifyInventoryQuantityToInventoryItemKey,
  ShopifyInventoryQuantityToInventoryLevelKey,
  ShopifyInventoryQuantityToLocationKey,
  ShopifyInventoryQuantityToShopKey,
  ShopifyLocationKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyInventoryQuantityKey,
  defaultFilterableApiIdentifiers: ["name", "adminGraphqlApiId"],
  "2025-04": {
    name: "Shopify Inventory Quantity",
    fields: [
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name", graphQLFieldKey: "name" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity", graphQLFieldKey: "quantity" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updatedAt", graphQLFieldKey: "updatedAt" } },
      },
      {
        name: "Inventory Level",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryQuantityToInventoryLevelKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "inventoryLevelId", graphQLFieldKey: "inventoryLevelId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyInventoryLevelKey,
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryQuantityToLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "locationId", graphQLFieldKey: "locationId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Inventory Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryQuantityToInventoryItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "inventoryItemId", graphQLFieldKey: "inventoryItemId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyInventoryItemKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryQuantityToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "inventoryQuantities" },
  },
  "2026-01": {
    fields: [
      {
        name: "Inventory Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryQuantityToInventoryItemKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "inventoryItemId", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyInventoryItemKey,
        },
        isDeleted: true,
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryQuantityToLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locationId", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
        isDeleted: true,
      },
    ],
  },
};

export const ShopifyInventoryQuantity = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
